import { useGlobalModalStore } from '@/stores/useGlobalModalStore/useGlobalModalStore'
import { TFunction } from 'i18next'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { ExportByEmailModalContent } from './ExportByEmailModalContent'
import { EmailExportReportGenerationModalContent } from './EmailExportReportGenerationModalContent'

type GetModalConfigsParams = {
    t: TFunction
    email: string
    setModalState: (state: { isOpen: boolean }) => void
    onExportByEmailClose: () => void
    onExportByEmailSuccess?: () => void
    onReportGenerationClose: () => void
    onSubmitExport?: () => Promise<void>
    onSubmitExportError?: (error: unknown) => void
}

const getModalConfigs = (params: GetModalConfigsParams) => {
    const {
        t,
        email,
        setModalState,
        onExportByEmailClose,
        onExportByEmailSuccess,
        onReportGenerationClose,
        onSubmitExport,
        onSubmitExportError,
    } = params

    return {
        exportByEmail: {
            isOpen: true,
            title: t('common.exportByEmail.title'),
            description: t('common.exportByEmail.description', { email }),
            content: () => (
                <ExportByEmailModalContent
                    onSuccess={() => {
                        setModalState({ isOpen: false })
                        onExportByEmailSuccess?.()
                    }}
                    onCancel={() => {
                        setModalState({ isOpen: false })
                        onExportByEmailClose()
                    }}
                    onSubmit={onSubmitExport}
                    onSubmitError={onSubmitExportError}
                />
            ),
            onClose: () => {
                setModalState({ isOpen: false })
                onExportByEmailClose()
            },
        },
        reportGeneration: {
            isOpen: true,
            title: t('common.emailExportReportGeneration.title'),
            description: t('common.emailExportReportGeneration.description', {
                email,
            }),
            icon: 'check_circle',
            iconClassName: '!text-linkPrimary',
            content: () => (
                <EmailExportReportGenerationModalContent
                    onClose={() => {
                        setModalState({ isOpen: false })
                        onReportGenerationClose()
                    }}
                />
            ),
            onClose: () => {
                setModalState({ isOpen: false })
                onReportGenerationClose()
            },
        },
    }
}

export type EmailExportModalProps = {
    exportByEmailOpen: boolean
    onExportByEmailClose: () => void
    onExportByEmailSuccess?: () => void
    reportGenerationOpen: boolean
    onReportGenerationClose: () => void
    onSubmitExport?: () => Promise<void>
    onSubmitExportError?: (error: unknown) => void
}

export const EmailExportModal: React.FC<EmailExportModalProps> = ({
    exportByEmailOpen,
    onExportByEmailClose,
    onExportByEmailSuccess,
    reportGenerationOpen,
    onReportGenerationClose,
    onSubmitExport,
    onSubmitExportError,
}) => {
    const { t } = useTranslation('translations')
    const setModalState = useGlobalModalStore((state) => state.setModalState)
    const user = useAuthStore((state) => state.user)
    const email = user?.email || ''

    const modalConfigs = useMemo(
        () =>
            getModalConfigs({
                t,
                email,
                setModalState,
                onExportByEmailClose,
                onExportByEmailSuccess,
                onReportGenerationClose,
                onSubmitExport,
                onSubmitExportError,
            }),
        [
            t,
            email,
            setModalState,
            onExportByEmailClose,
            onExportByEmailSuccess,
            onReportGenerationClose,
            onSubmitExport,
            onSubmitExportError,
        ]
    )

    useEffect(() => {
        if (exportByEmailOpen) {
            setModalState(modalConfigs.exportByEmail)
            return
        }
        if (reportGenerationOpen) {
            setModalState(modalConfigs.reportGeneration)
        } else {
            setModalState({ isOpen: false })
        }
    }, [exportByEmailOpen, reportGenerationOpen, modalConfigs, setModalState])

    return null
}
